import React, { useState } from 'react';
import { Modal, Upload, Table, Button, message, Alert, Space, Typography } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import {
  customerApi,
  type CustomerDTO,
  type CustomerImportAppendItemDTO,
  type CustomerImportPreviewDTO,
} from '../api/kfc';

interface CustomerImportModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const addressLabel = (a: {
  label?: string;
  remark?: string;
  contactName?: string;
  phone?: string;
  address?: string;
  postalCode?: string;
}) =>
  [a.label, a.remark, a.contactName, a.phone, a.address, a.postalCode].filter(Boolean).join(' / ') || '-';

const CustomerImportModal: React.FC<CustomerImportModalProps> = ({ open, onClose, onSuccess }) => {
  const [preview, setPreview] = useState<CustomerImportPreviewDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const reset = () => {
    setPreview(null);
    setLoading(false);
    setConfirming(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleUpload = (file: File) => {
    setLoading(true);
    customerApi
      .importPreview(file)
      .then((res) => {
        setPreview(res.data);
        const s = res.data.summary;
        message.info(
          `解析完成：新增 ${s.create}，追加地址 ${s.appendAddress}，无变化 ${s.unchanged}，错误 ${s.error}`
        );
      })
      .catch((e) => message.error(e.response?.data?.message || '解析失败'))
      .finally(() => setLoading(false));
    return false;
  };

  const handleConfirm = () => {
    if (!preview) return;
    const toCreate = preview.toCreate || [];
    const toAppendAddress = preview.toAppendAddress || [];
    if (!toCreate.length && !toAppendAddress.length) {
      message.info('没有需要导入的变更');
      return;
    }
    setConfirming(true);
    customerApi
      .importConfirm({ toCreate, toAppendAddress })
      .then((res) => {
        message.success(
          `导入完成：新增客户 ${res.data.created}，追加地址 ${res.data.appendedAddress}`
        );
        handleClose();
        onSuccess();
      })
      .catch((e) => message.error(e.response?.data?.message || '导入失败'))
      .finally(() => setConfirming(false));
  };

  return (
    <Modal
      title="导入客户"
      open={open}
      onCancel={handleClose}
      width={920}
      footer={
        preview ? (
          <Space>
            <Button onClick={() => setPreview(null)}>重新选择文件</Button>
            <Button onClick={handleClose}>取消</Button>
            <Button
              type="primary"
              loading={confirming}
              disabled={!preview.toCreate?.length && !preview.toAppendAddress?.length}
              onClick={handleConfirm}
            >
              确认导入
            </Button>
          </Space>
        ) : (
          <Button onClick={handleClose}>关闭</Button>
        )
      }
    >
      {!preview ? (
        <Upload accept=".xlsx,.xls" showUploadList={false} beforeUpload={handleUpload}>
          <Button icon={<UploadOutlined />} loading={loading}>
            选择 Excel 文件
          </Button>
        </Upload>
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              <Typography.Text>
                新增 {preview.summary.create} 个客户，追加地址 {preview.summary.appendAddress} 个客户，无变化{' '}
                {preview.summary.unchanged} 个
                {preview.summary.error > 0 ? `，错误 ${preview.summary.error} 条` : ''}
              </Typography.Text>
            }
          />
          {preview.errors?.length > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="以下行无法导入"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {preview.errors.map((e, i) => (
                    <li key={`${e.row}-${i}`}>
                      第 {e.row} 行：{e.message}
                    </li>
                  ))}
                </ul>
              }
            />
          )}
          {preview.toCreate?.length > 0 && (
            <>
              <Typography.Title level={5}>新增客户（{preview.toCreate.length}）</Typography.Title>
              <Table
                size="small"
                rowKey={(r: CustomerDTO) => r.customerCode}
                dataSource={preview.toCreate}
                pagination={{ pageSize: 5 }}
                scroll={{ x: 800 }}
                columns={[
                  { title: '客户标识', dataIndex: 'customerCode', width: 110 },
                  { title: '客户名称', dataIndex: 'name', width: 120, ellipsis: true },
                  { title: '域名', dataIndex: 'domain', width: 120, ellipsis: true },
                  { title: '邮箱', dataIndex: 'email', width: 140, ellipsis: true },
                  { title: '电话', dataIndex: 'phone', width: 110 },
                  {
                    title: '地址数',
                    key: 'addrCount',
                    width: 80,
                    render: (_, r) => r.addresses?.length || 0,
                  },
                ]}
                style={{ marginBottom: 16 }}
              />
            </>
          )}
          {preview.toAppendAddress?.length > 0 && (
            <>
              <Typography.Title level={5}>
                追加地址（{preview.toAppendAddress.length}）
              </Typography.Title>
              <Table
                size="small"
                rowKey={(r: CustomerImportAppendItemDTO) =>
                  r.customerId || r.customerCode || Math.random().toString()
                }
                dataSource={preview.toAppendAddress}
                pagination={{ pageSize: 5 }}
                scroll={{ x: 800 }}
                columns={[
                  { title: '客户标识', dataIndex: 'customerCode', width: 110 },
                  { title: '客户名称', dataIndex: 'name', width: 120, ellipsis: true },
                  {
                    title: '新增地址',
                    key: 'addresses',
                    render: (_, r) =>
                      (r.addresses || []).map((a, i) => <div key={i}>{addressLabel(a)}</div>),
                  },
                ]}
              />
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default CustomerImportModal;
